import { useEffect, useState } from "react";
import { AdminHeader } from "../../components/header";
import {
  formatFirestoreTimestamp,
  formatMoney,
  statusColor,
} from "../../utils";
import { CustomSelect } from "../../components/custom.select";
import {
  collection,
  DocumentData,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  startAfter,
  Timestamp,
  where,
} from "firebase/firestore";
import Skeleton from "react-loading-skeleton";
import { Pagination } from "../../components/pagination";
import { toast } from "react-toastify";
import { firestoredb } from "../../utils/firebase";
import moment from "moment";
import { LoadingPage } from "../../components/loading.page";

export function InternalEvents() {
  const [stats, setStats] = useState<{
    eventCount: number;
    ticketsSales: number;
    ticketsSold: number;
    payout: number;
    usersCount: number;
    balance: number;
  }>({
    eventCount: 0,
    ticketsSales: 0,
    ticketsSold: 0,
    payout: 0,
    usersCount: 0,
    balance: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [tableLoading, setTableLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [events, setEvents] = useState<DocumentData[] | []>([]);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 15,
    total: 0,
  });
  const statuses = [
    { text: "All", value: "All" },
    { text: "Completed", value: "Completed" },
    { text: "On-going", value: "On-going" },
  ];

  useEffect(() => {
    allFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, debouncedSearchTerm]);

  useEffect(() => {
    // debounces search address and tracks when store address is entered
    // if (!searchTerm.length) return setSearchedUserIds([]);
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const allFetch = async () => {
    try {
      await Promise.all([fetchEvents(), fetchStats()]);
    } catch (error) {
    } finally {
      setIsPageLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setTableLoading(true);
      const eventsQuerySnapshot = await getDocs(
        getEventsWithFiltersApplied([])
      );
      const events = eventsQuerySnapshot.docs.map((el) => {
        let eventEndsAt = el.data()?.endDate?.seconds;
        if (eventEndsAt) {
          eventEndsAt = new Date(eventEndsAt * 1000);
          eventEndsAt.setHours(23, 59, 59, 999);
        }
        if (!eventEndsAt) {
          const date = new Date(el.data()?.date?.seconds * 1000);
          date.setDate(date.getDate() + 1);
          date.setHours(0, 0, 0, 0);
          eventEndsAt = date;
        }
        return {
          ...el.data(),
          id: el.id,
          completed: moment(eventEndsAt).isBefore(moment()) ?? false,
        };
      });
      setEvents([...events]);
      setTableLoading(false);
    } catch (error: any) {
      if (error?.["code"] === "permission-denied") {
        return (window.location.href = "/internal/login");
      }
      setTableLoading(false);
    }
  };

  const fetchStats = async () => {
    if (stats.eventCount) return;
    try {
      const allEventsCollectionRef = collection(firestoredb, "events");
      const ticketsCollectionRef = collection(firestoredb, "tickets");
      const usersCollectionRef = collection(firestoredb, "users");
      const payoutsCollectionRef = collection(firestoredb, "payouts");
      const [
        allEventsQuerySnapshot,
        ticketsQuerySnapshot,
        usersQuerySnapshot,
        payoutsQuerySnapshot,
      ] = await Promise.all([
        getDocs(allEventsCollectionRef),
        getDocs(ticketsCollectionRef),
        getDocs(usersCollectionRef),
        getDocs(payoutsCollectionRef),
      ]);
      const totalPayout = payoutsQuerySnapshot.docs.reduce((acc, curr) => {
        return acc + (curr.data().amount ?? 0);
      }, 0);
      const ticketSales = ticketsQuerySnapshot.docs.reduce((acc, curr) => {
        const data = curr.data();
        let amount =
          (data.priceAfterDiscount ?? data.price ?? 0) -
          (data?.feeOnOrganiser / data?.boughtAtOnce || 0);
        amount = amount > 0 ? amount : 0;
        return acc + amount;
      }, 0);
      setStats({
        ...stats,
        eventCount: allEventsQuerySnapshot.size,
        ticketsSold: ticketsQuerySnapshot.size,
        usersCount: usersQuerySnapshot.size,
        payout: totalPayout,
        ticketsSales: ticketSales,
        balance: ticketSales - totalPayout > 0 ? ticketSales - totalPayout : 0,
      });
      setPagination({ ...pagination, total: allEventsQuerySnapshot.size });
    } catch (error: any) {
      if (error?.["code"] === "permission-denied") {
        return (window.location.href = "/internal/login");
      }
    }
  };

  const getEventsWithFiltersApplied = (paramConstraints: QueryConstraint[]) => {
    const eventsCollectionRef = collection(firestoredb, "events");
    let conditionalConstraints: QueryConstraint[] = [];
    if (selectedStatus.toLowerCase() !== "all") {
      if (selectedStatus.toLowerCase() === "completed") {
        conditionalConstraints = [
          ...conditionalConstraints,
          where(
            "date",
            "<",
            Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000))
          ),
        ];
      }
      if (selectedStatus.toLowerCase() === "on-going") {
        conditionalConstraints = [
          ...conditionalConstraints,
          where("date", ">=", Timestamp.fromDate(new Date())),
        ];
      }
    }
    if (debouncedSearchTerm.length) {
      conditionalConstraints = [
        // where("title", ">=", debouncedSearchTerm),
        // where("title", "==", debouncedSearchTerm),
      ];
    }
    const eQuery = query(
      eventsCollectionRef,
      orderBy("date", "desc"),
      limit(pagination.limit),
      ...conditionalConstraints,
      ...paramConstraints
    );
    return eQuery;
  };

  const handlePagination = async (page: number) => {
    try {
      setTableLoading(true);
      const skipQuery = await getEventsWithFiltersApplied([
        limit((page > 0 ? page : 1) * pagination.limit),
      ]);
      const skipSnap = await getDocs(skipQuery);
      const docs = skipSnap.docs;
      const lastDoc = docs[docs.length - 1];
      let q;
      if (lastDoc && page)
        q = await getEventsWithFiltersApplied([startAfter(lastDoc)]);
      else q = await getEventsWithFiltersApplied([]);
      const eventQuerySnapshot = await getDocs(q);
      const nextEvents = eventQuerySnapshot.docs.map((el) => ({
        ...el.data(),
        id: el.id,
        completed: moment(
          (el.data().endDate ?? el.data().date).toDate()
        ).isBefore(moment().toDate()),
      }));
      setEvents(nextEvents);
      setPagination({ ...pagination, page });
    } catch (error) {
      toast.error("Cannot move to the page selected");
      console.log("pagination error", error);
    } finally {
      setTableLoading(false);
    }
  };

  return isPageLoading ? (
    <LoadingPage />
  ) : (
    <div className='dashboard'>
      <AdminHeader type='internal-admin' />
      <div className='dashboard-header desktop'>
        <div className='dashboard-header__title'>
          <h1>Events Internal Admin</h1>
        </div>
        <div className='dashboard-header__actions'>
          <button
            className='btn'
            onClick={() =>
              (window.location.href = "/internal/admin/create-event")
            }
          >
            Create Event
          </button>
        </div>
      </div>
      <div className='dashboard-content desktop'>
        <div className='ticket-cards'>
          <div className='tickets-board'>
            <div className='ticket-container'>
              <div className='title'>Events</div>
              <div className='ticket-container__content'>
                <div className='ticket-container__content-figures'>
                  <h4 className='count'>
                    {formatMoney(stats?.eventCount ?? 0)}
                  </h4>
                  <p>Number of events</p>
                </div>
              </div>
            </div>
            <div className='ticket-container'>
              <div className='title'>Tickets sold</div>
              <div className='ticket-container__content'>
                <div className='ticket-container__content-figures'>
                  <h4 className='count'>
                    {formatMoney(stats?.ticketsSold ?? 0)}
                  </h4>
                  <p>Number of tickets sold</p>
                </div>
              </div>
            </div>
            <div className='ticket-container'>
              <div className='title'>Users</div>
              <div className='ticket-container__content'>
                <div className='ticket-container__content-figures'>
                  <h4 className='count'>
                    {formatMoney(stats?.usersCount ?? 0)}
                  </h4>
                  <p>Number of unique users</p>
                </div>
              </div>
            </div>
          </div>
          <div className='tickets-board'>
            <div className='ticket-container'>
              <div className='title'>Ticket Sales</div>
              <div className='ticket-container__content'>
                <div className='ticket-container__content-figures'>
                  <h4 className='count'>
                    ₦{formatMoney(stats?.ticketsSales ?? 0)}
                  </h4>
                  <p>Value of tickets sold</p>
                </div>
              </div>
            </div>
            <div className='ticket-container'>
              <div className='title'>Payout</div>
              <div className='ticket-container__content'>
                <div className='ticket-container__content-figures'>
                  <h4 className='count'>₦{formatMoney(stats?.payout ?? 0)}</h4>
                  <p>Money paid out to organisers</p>
                </div>
              </div>
            </div>
            <div className='ticket-container'>
              <div className='title'>Balance</div>
              <div className='ticket-container__content'>
                <div className='ticket-container__content-figures'>
                  <h4 className='count'>₦{formatMoney(stats?.balance ?? 0)}</h4>
                  <p>Balance to be paid out</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='ticket-filters'>
          <div className='ticket-filters__text-search'>
            <i className='bi bi-search'></i>
            <input
              placeholder='Search name, email...'
              onChange={({ target: { value } }) => setSearchTerm(value)}
              value={searchTerm}
            />
          </div>
          <div className='ticket-filters__filter-search'>
            <CustomSelect
              title='Status:'
              titleColor=''
              selectedOption={
                statuses.find((type) => type.value === selectedStatus)?.text ??
                "All"
              }
              options={statuses}
              onChange={(value) => setSelectedStatus(value)}
            />
          </div>
        </div>
        <div className='ticket-table'>
          <table className='table'>
            <thead>
              <tr>
                <th scope='col'>Event name</th>
                <th scope='col'>Event date</th>
                <th scope='col'>Status</th>
              </tr>
            </thead>
            {tableLoading ? (
              <tbody>
                {[1, 2, 3, 4, 5].map((_, key) => (
                  <tr key={key}>
                    <td colSpan={7}>
                      <Skeleton width={"100%"} style={{ marginTop: "5px" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : events.length ? (
              <>
                <tbody>
                  {events.map((event, key) => (
                    <tr
                      key={key}
                      onClick={() =>
                        window.open(
                          `${window.location.origin}/internal/admin/dashboard/${event.id}`,
                          "_blank"
                        )
                      }
                    >
                      <td>{event?.title || "-"}</td>
                      <td>{formatFirestoreTimestamp(event?.date)}</td>
                      <td>
                        <span
                          className={`ticket-type ${statusColor(
                            event?.completed ? "completed" : "going"
                          )}`}
                        >
                          {event?.completed ? "Completed" : "On-going"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <tbody>
                <tr className='no-tickets'>
                  <td colSpan={3}>
                    <div style={{ width: "100%" }}>No Data!</div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
          {events.length ? (
            <Pagination
              pageCount={Number(Math.ceil(pagination.total / pagination.limit))}
              onPageChange={(el) => handlePagination(el)}
              page={Number(pagination.page)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
